import { InputProps, OutlinedInput, Stack, styled } from '@mui/material';
import { QueryBuilderDnD } from '@react-querybuilder/dnd';
import {
  defaultMuiComponents,
  MaterialActionProps,
  QueryBuilderMaterial,
} from '@react-querybuilder/material';
import { useEffect, useMemo } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { QueryBuilder } from 'react-querybuilder';
import 'react-querybuilder/dist/query-builder.css';
import { useSelectedPartner } from 'src/graphql';
import {
  CustomViewFormConditionField,
  CustomViewFormModel,
} from 'src/graphql/entities/custom-view';
import { getOperatorsByUiType } from 'src/graphql/entities/custom-view/custom-view-config/operators';
import {
  mapValuesToOptions,
  resolveFieldOptions,
  useCustomViewFilterOptions,
} from 'src/graphql/entities/custom-view/custom-view-config/use-custom-view-options';
import { conditionsCustomControls } from 'src/pages/Settings/rules/rules-form-config';

const CONTROL_ELEMENTS = {
  ...conditionsCustomControls,
  addGroupAction: () => null,
};

const muiComponents = {
  ...defaultMuiComponents,
  Input: (props: InputProps) => <OutlinedInput size="small" {...props} />,
} as MaterialActionProps['muiComponents'];

export const RulesFormContainer = styled(Stack, {
  name: 'RulesFormContainer',
})(() => ({
  '& .ruleGroup': {
    flexDirection: 'column-reverse',
    border: 'none',
    background: 'none',
  },

  '& .ruleGroup-body': {
    '& .betweenRules, & .betweenRules .MuiInputBase-formControl': {
      minWidth: '5rem',
      maxWidth: '20% !important',
    },

    '.MuiStack-root': {
      maxWidth: '16rem',
      flex: '1 0 auto',
    },

    '.MuiStack-root .MuiFormControl-root': {
      maxWidth: '16rem',

      flex: '1 0 auto',
    },

    '& .rule-operators': {
      width: '10rem',
      maxWidth: '30% !important',
    },

    '& .rule-value': {
      flex: '1 1 auto',
    },

    display: 'grid !important',
    alignItems: 'start',
    alignSelf: 'start',
    gridTemplateColumns: 'repeat(N - 1, auto) 1fr',

    '& .queryBuilder-dragHandle': {
      display: 'flex',
      alignItems: 'center',
    },
  },

  '& .ruleGroup-body > .rule:first-of-type:not(:only-child), & .ruleGroup-body > .ruleGroup:first-of-type:not(:only-child)':
    {
      gridColumnStart: 2,
    },
}));

export const CustomViewsQueryBuilder = ({
  name,
  onChange,
  filterAttributesList,
}: {
  name: string;
  filterAttributesList: CustomViewFormConditionField[];
  onChange?: (values: CustomViewFormModel['conditions']) => void;
}) => {
  const { control } = useFormContext();
  const { selectedPartner } = useSelectedPartner();

  const query = useWatch({
    control,
    name,
  }) as CustomViewFormModel['conditions'];

  useEffect(() => {
    if (query.rules?.length) {
      onChange?.(query);
    }
  }, [query, onChange]);

  const queryOptions = useCustomViewFilterOptions({
    partnerId: selectedPartner?.partnerId,
    conditions: query.rules,
  });

  const mappedFields = useMemo(() => {
    return {
      ...query,
      rules: query.rules.map((rule) => {
        if (!Array.isArray(rule.value)) {
          return rule;
        }

        const anyOptions = resolveFieldOptions(rule.field, queryOptions);

        if (!anyOptions.length) {
          return rule;
        }

        return {
          ...rule,
          value: mapValuesToOptions(rule.value, anyOptions),
        };
      }),
    };
  }, [query, queryOptions]);

  return (
    <RulesFormContainer>
      <QueryBuilderDnD>
        <QueryBuilderMaterial muiComponents={muiComponents}>
          <Controller
            control={control}
            name={name}
            rules={{ required: true }}
            render={({ field }) => (
              <QueryBuilder
                // @ts-expect-error query builder expects a string, but actually fields are object
                getDefaultField={(items) => items[0]}
                enableDragAndDrop
                enableMountQueryChange={false}
                resetOnFieldChange
                resetOnOperatorChange
                context={{
                  timezone: selectedPartner?.timeZone ?? 'America/New_York',
                  queryOptions,
                }}
                autoSelectField
                showCombinatorsBetweenRules
                query={mappedFields}
                onQueryChange={field.onChange}
                fields={filterAttributesList ?? []}
                // @ts-expect-error Overriding default library typings because of custom fields structure.
                getOperators={(f: CustomViewFormConditionField) =>
                  getOperatorsByUiType(f?.uiType)
                }
                // @ts-expect-error Overriding default library typings because of custom fields structure.
                controlElements={CONTROL_ELEMENTS}
              />
            )}
          />
        </QueryBuilderMaterial>
      </QueryBuilderDnD>
    </RulesFormContainer>
  );
};
